/**
 * Client breadcrumb capture. Records what the user actually did - screens
 * entered, elements tapped, fields edited, scrolling, JS errors, and the app's
 * own view of every API call - buffers it in memory and posts batches to
 * `POST /dev/ui-events`, which stores them in `dev_ui_events` (artham-telemetry).
 * `pnpm devlog:ui` in apps/api reads them beside the matching `dev_request_log`
 * rows, so a failed flow reads as "tapped X, typed Y, call Z returned 422".
 *
 * Unlike the X-Debug-Surface header in ./dev-telemetry (dev builds only), this
 * runs in release builds too, gated on VITE_UI_TELEMETRY. That is deliberate:
 * the thing being debugged is a signed APK talking to the deployed API. Turn it
 * off by dropping the flag from .env.production and rebuilding.
 *
 * Field values are recorded verbatim apart from password and hidden inputs. This
 * is a pre-launch debug aid and the OTP is usually the exact thing you need to
 * read back; the whole capture is switched off for launch.
 */

import axiosInstance from './axios-artham';

// Flush on whichever trips first. 4s keeps the devlog readable as a live feed;
// 40 events keeps a batch small enough to be irrelevant on a mobile connection.
const FLUSH_MAX_AGE_MS = 4_000;
const FLUSH_MAX_EVENTS = 40;
// Drop oldest beyond this if the sink is unreachable (offline, API down).
const BUFFER_HARD_CAP = 1_000;
const SCROLL_THROTTLE_MS = 1_000;
// Identical console lines past this are dropped, per session.
const CONSOLE_REPEAT_LIMIT = 3;
const VALUE_CAP = 120;
const INGEST_PATH = '/dev/ui-events';

type Kind = 'screen' | 'click' | 'input' | 'scroll' | 'error' | 'console' | 'net' | 'lifecycle';

interface UiEvent {
  ts: number;
  seq: number;
  kind: Kind;
  surface?: string;
  target?: string;
  label?: string;
  value?: string;
  detail?: string;
  requestId?: string;
}

let started = false;
let seq = 0;
let buffer: UiEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;
let lastScrollAt = 0;
let lastScreen = '';

const sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export function isUiTelemetryEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_UI_TELEMETRY === 'true';
}

function currentSurface(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname + window.location.hash;
}

const cap = (s: string, n = VALUE_CAP): string =>
  s.length > n ? `${s.slice(0, n)}…(+${s.length - n})` : s;

const platform = (() => {
  const c = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  return c?.getPlatform?.() ?? 'web';
})();

/** Short, greppable description of a DOM element: what it is and how to find it. */
function describe(el: Element | null): { target: string; label: string } {
  if (!el) return { target: '', label: '' };
  const tag = el.tagName.toLowerCase();
  const testId = el.getAttribute('data-testid');
  const id = el.id;
  const name = el.getAttribute('name');
  const role = el.getAttribute('role');
  const aria = el.getAttribute('aria-label');
  const type = el.getAttribute('type');

  let target = tag;
  if (testId) target += `[data-testid=${testId}]`;
  else if (name) target += `[name=${name}]`;
  else if (id) target += `#${id}`;
  else if (role) target += `[role=${role}]`;
  if (type && !testId && !name && !id) target += `[type=${type}]`;

  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  return { target: cap(target, 160), label: cap(aria || text, 160) };
}

/** Nearest thing a user would call "the button they pressed". */
function interactiveAncestor(el: Element | null): Element | null {
  const sel = 'button,a,[role="button"],[role="tab"],[role="menuitem"],input,select,textarea,label';
  return el?.closest?.(sel) ?? el;
}

function fieldValue(el: Element): string | undefined {
  const input = el as HTMLInputElement;
  const type = (input.type ?? '').toLowerCase();
  if (type === 'password' || type === 'hidden') return undefined;
  const raw = input.value;
  if (typeof raw !== 'string') return undefined;
  if (type === 'checkbox' || type === 'radio') return String(input.checked);
  return cap(raw);
}

function push(kind: Kind, e: Partial<UiEvent> = {}): void {
  seq += 1;
  buffer.push({ ts: Date.now(), seq, kind, surface: currentSurface(), ...e });
  if (buffer.length > BUFFER_HARD_CAP) buffer.splice(0, buffer.length - BUFFER_HARD_CAP);
  if (buffer.length >= FLUSH_MAX_EVENTS) void flush();
  else armTimer();
}

/** Public hook for anything worth marking by hand from a component. */
export function trackUiEvent(label: string, detail?: unknown): void {
  if (!started) return;
  push('lifecycle', { label: cap(label, 160), detail: detail ? cap(safeJson(detail), 2000) : undefined });
}

function safeJson(v: unknown): string {
  try {
    return typeof v === 'string' ? v : JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function armTimer(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_MAX_AGE_MS);
}

async function flush(useKeepalive = false): Promise<void> {
  if (flushing || !buffer.length) return;
  flushing = true;
  const events = buffer;
  buffer = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const payload = {
    sessionId,
    appVersion: import.meta.env.VITE_APP_VERSION ?? 'dev',
    platform,
    events,
  };
  try {
    if (useKeepalive) {
      // Page is going away - axios can't outlive it, fetch(keepalive) can.
      const auth = axiosInstance.defaults.headers.common.Authorization;
      await fetch(`${axiosInstance.defaults.baseURL ?? ''}${INGEST_PATH}`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(typeof auth === 'string' ? { Authorization: auth } : {}),
        },
        body: JSON.stringify(payload),
      });
    } else {
      await axiosInstance.post(INGEST_PATH, payload);
    }
  } catch {
    // Sink unreachable (offline, API down). Put the batch back at the front so
    // it lands on the next flush; the hard cap stops this growing forever.
    buffer = [...events, ...buffer].slice(-BUFFER_HARD_CAP);
  } finally {
    flushing = false;
  }
}

/** Screen changes, including the ones React Router makes via history. */
function installRouteCapture(): void {
  const emit = (how: string): void => {
    const now = currentSurface();
    if (now === lastScreen) return;
    const from = lastScreen;
    lastScreen = now;
    push('screen', { target: now, label: how, detail: from ? `from ${from}` : undefined });
  };

  const wrap = (fn: History['pushState']): History['pushState'] =>
    function patched(this: History, ...args: Parameters<History['pushState']>) {
      const out = fn.apply(this, args);
      queueMicrotask(() => emit('push'));
      return out;
    };
  history.pushState = wrap(history.pushState);
  history.replaceState = wrap(history.replaceState);
  window.addEventListener('popstate', () => emit('pop'));
  emit('load');
}

function installInteractionCapture(): void {
  document.addEventListener(
    'click',
    (ev) => {
      const el = interactiveAncestor(ev.target as Element | null);
      const { target, label } = describe(el);
      if (!target) return;
      push('click', { target, label });
    },
    true
  );

  // focusout rather than every keystroke: one row per field the user finished
  // editing, which is what you want when reading a form back.
  document.addEventListener(
    'focusout',
    (ev) => {
      const el = ev.target as Element | null;
      if (!el || !/^(input|textarea|select)$/i.test(el.tagName)) return;
      const { target, label } = describe(el);
      push('input', { target, label, value: fieldValue(el) });
    },
    true
  );

  document.addEventListener(
    'change',
    (ev) => {
      const el = ev.target as Element | null;
      if (!el || !/^(select|input)$/i.test(el.tagName)) return;
      const type = (el as HTMLInputElement).type?.toLowerCase();
      if (type !== 'checkbox' && type !== 'radio' && el.tagName.toLowerCase() !== 'select') return;
      const { target, label } = describe(el);
      push('input', { target, label, value: fieldValue(el) });
    },
    true
  );

  document.addEventListener(
    'scroll',
    (ev) => {
      const now = Date.now();
      if (now - lastScrollAt < SCROLL_THROTTLE_MS) return;
      lastScrollAt = now;
      const el = ev.target as Element | Document | null;
      const node = el instanceof Element ? el : document.scrollingElement;
      const top = node instanceof Element ? Math.round(node.scrollTop) : Math.round(window.scrollY);
      const { target } = describe(node instanceof Element ? node : null);
      push('scroll', { target: target || 'window', value: String(top) });
    },
    true
  );
}

function installErrorCapture(): void {
  window.addEventListener('error', (ev) => {
    push('error', {
      label: cap(ev.message ?? 'error', 300),
      detail: cap(`${ev.filename ?? ''}:${ev.lineno ?? 0} ${ev.error?.stack ?? ''}`, 2000),
    });
  });

  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason;
    push('error', {
      label: cap(`unhandledrejection: ${r?.message ?? safeJson(r)}`, 300),
      detail: cap(r?.stack ?? '', 2000),
    });
  });

  // Capacitor plugins emit the same deprecation warning on every listener
  // registration - a dozen identical lines per launch would bury everything
  // else. Keep the first few of any repeated message, then stop.
  const seen = new Map<string, number>();
  (['error', 'warn'] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      original(...args);
      const text = args.map(safeJson).join(' ');
      const n = (seen.get(text) ?? 0) + 1;
      seen.set(text, n);
      if (n > CONSOLE_REPEAT_LIMIT) return;
      push('console', {
        label: level,
        value: cap(text, 200),
        detail: cap(text + (n === CONSOLE_REPEAT_LIMIT ? ' [further repeats suppressed]' : ''), 2000),
      });
    };
  });
}

/**
 * The app's own view of each API call: what it asked for, what came back, how
 * long it waited. `requestId` joins the row to `dev_request_log`, so a call the
 * app saw as "Network Error" can be matched to what the worker actually did.
 */
function installNetworkCapture(): void {
  axiosInstance.interceptors.request.use((config) => {
    (config as { __t0?: number }).__t0 = Date.now();
    return config;
  });

  const record = (
    config: { url?: string; method?: string; __t0?: number } | undefined,
    status: number | string,
    headers: unknown,
    extra?: string
  ): void => {
    const url = config?.url ?? '';
    if (url.includes(INGEST_PATH)) return;
    const started_ = config?.__t0 ?? Date.now();
    const requestId = (headers as Record<string, string> | undefined)?.['x-request-id'];
    push('net', {
      target: `${(config?.method ?? 'get').toUpperCase()} ${cap(url, 200)}`,
      label: String(status),
      value: `${Date.now() - started_}ms`,
      detail: extra,
      requestId,
    });
  };

  axiosInstance.interceptors.response.use(
    (res) => {
      record(res.config as never, res.status, res.headers);
      return res;
    },
    (error) => {
      record(
        error.config as never,
        error.response?.status ?? error.code ?? 'ERR',
        error.response?.headers,
        cap(`${error.message ?? ''} ${safeJson(error.response?.data ?? '')}`, 2000)
      );
      return Promise.reject(error);
    }
  );
}

/**
 * What the user was actually shown. Watching the DOM rather than wrapping the
 * toast helper catches every source at once - sonner toasts, MUI <Alert>, and
 * any `role="alert"` - and keeps working when a new one is introduced. Without
 * this the log holds the API's error while the screen showed something else
 * entirely, and there is no way to tell which the user acted on.
 */
const ALERT_SELECTOR = '[data-sonner-toast], [role="alert"], .MuiAlert-root';

function installAlertCapture(): void {
  const seen = new WeakSet<Element>();

  const record = (el: Element): void => {
    if (seen.has(el)) return;
    seen.add(el);
    const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return;
    // sonner marks severity with data-type; MUI uses a severity class.
    const kind =
      el.getAttribute('data-type') ??
      (el.className.match(/MuiAlert-(?:standard|filled|outlined)(\w+)/)?.[1] ?? '').toLowerCase() ??
      '';
    push('lifecycle', { target: 'alert', label: kind || 'alert', value: cap(text, 400) });
  };

  const scan = (node: Node): void => {
    if (!(node instanceof Element)) return;
    if (node.matches?.(ALERT_SELECTOR)) record(node);
    node.querySelectorAll?.(ALERT_SELECTOR).forEach(record);
  };

  new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach(scan);
      // A toast that swaps its text in place adds no node, so re-read the target.
      if (r.type === 'characterData' && r.target.parentElement) scan(r.target.parentElement);
    }
  }).observe(document.body, { childList: true, subtree: true, characterData: true });

  scan(document.body);
}

function installLifecycleCapture(): void {
  document.addEventListener('visibilitychange', () => {
    push('lifecycle', { label: `visibility:${document.visibilityState}` });
    if (document.visibilityState === 'hidden') void flush(true);
  });
  window.addEventListener('pagehide', () => {
    push('lifecycle', { label: 'pagehide' });
    void flush(true);
  });
  window.addEventListener('online', () => push('lifecycle', { label: 'online' }));
  window.addEventListener('offline', () => push('lifecycle', { label: 'offline' }));
}

/** Idempotent; safe to call from a React StrictMode double-mount. */
export function initUiTelemetry(): void {
  if (started || !isUiTelemetryEnabled() || typeof window === 'undefined') return;
  started = true;

  installRouteCapture();
  installInteractionCapture();
  installErrorCapture();
  installNetworkCapture();
  installAlertCapture();
  installLifecycleCapture();

  push('lifecycle', {
    label: 'session-start',
    detail: cap(`${platform} ${navigator.userAgent}`, 500),
  });
}
